"use client";

import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { usePathname } from "next/navigation";
import EnqueryForm from "@/components/forms/EnqueryForm";
import { FormHeading, SuccessMessageType } from "@/interface/types";
import { googleTagManager } from "@/components/common/utils";
import {
  useWebinarSchedule,
  useWebinarScheduleLoading,
} from "@/contexts/EventContext";
import { getWebinarEntry, getOrdinalDateParts } from "@/data/webinarSchedule";
import WebinarDateSkeleton from "@/components/common/WebinarDateSkeleton";

interface StickyFooterProps {
  pageName: string;
  courseID: number;
}

const StickyFooter: React.FC<StickyFooterProps> = ({ pageName, courseID }) => {
  const pathname = usePathname();
  const shouldHideWidget = pathname === "/bengaluru";

  const webinarSchedule = useWebinarSchedule();
  const isScheduleLoading = useWebinarScheduleLoading();
  const weeklyWebinarEntry = getWebinarEntry("weekly", webinarSchedule);
  const weeklyDateParts = getOrdinalDateParts(weeklyWebinarEntry.date);

  const [isOpen, setIsOpen] = useState(false);

  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartY = useRef(0);

  const [barDragY, setBarDragY] = useState(0);
  const [isBarDragging, setIsBarDragging] = useState(false);
  const [isBarDismissed] = useState(false);
  const [barPointerDown, setBarPointerDown] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isDrawerSuccess, setIsDrawerSuccess] = useState(false);

  const barDragStartY = useRef(0);
  const isOpenRef = useRef(isOpen);
  const isBarDraggingRef = useRef(false);

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (!barPointerDown) return;

    const onMouseMove = (e: MouseEvent) => {
      const delta = e.clientY - barDragStartY.current;
      if (!isBarDraggingRef.current && delta < -10) {
        isBarDraggingRef.current = true;
        setIsBarDragging(true);
      }
      if (isBarDraggingRef.current && delta < -50 && !isOpenRef.current) {
        isBarDraggingRef.current = false;
        setIsBarDragging(false);
        setBarDragY(0);
        setBarPointerDown(false);
        setIsOpen(true);
      }
    };

    const onMouseUp = () => {
      isBarDraggingRef.current = false;
      setIsBarDragging(false);
      setBarPointerDown(false);
      setBarDragY(0);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, [barPointerDown]);

  const handleEnquireNow = () => {
    googleTagManager("CMA_LP_F_EnquireNow", {
      button_location: "sticky_footer",
      page_url: typeof window !== "undefined" ? window.location.href : "",
    });
    setIsOpen(true);
  };

  const closePanel = (immediate = false) => {
    if (immediate) {
      setIsOpen(false);
      setDragY(0);
      setIsDragging(false);
      return;
    }
    setIsDragging(false);
    setDragY(typeof window !== "undefined" ? window.innerHeight : 800);
    setTimeout(() => {
      setIsOpen(false);
      setDragY(0);
    }, 320);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    dragStartY.current = e.touches[0].clientY;
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const delta = e.touches[0].clientY - dragStartY.current;
    if (delta > 0) setDragY(delta);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    if (dragY > 100) {
      closePanel();
    } else {
      setDragY(0);
    }
  };

  const handleBarTouchStart = (e: React.TouchEvent) => {
    barDragStartY.current = e.touches[0].clientY;
  };

  const handleBarTouchMove = (e: React.TouchEvent) => {
    const delta = e.touches[0].clientY - barDragStartY.current;
    if (!isBarDraggingRef.current && delta < -10) {
      isBarDraggingRef.current = true;
      setIsBarDragging(true);
    }
    if (delta < -50 && !isOpenRef.current) {
      isBarDraggingRef.current = false;
      setIsBarDragging(false);
      setBarDragY(0);
      setIsOpen(true);
    }
  };

  const handleBarTouchEnd = () => {
    isBarDraggingRef.current = false;
    setIsBarDragging(false);
    setBarDragY(0);
  };

  const handleBarMouseDown = (e: React.MouseEvent) => {
    barDragStartY.current = e.clientY;
    setBarPointerDown(true);
  };

  if (shouldHideWidget) return null;
  if (isBarDismissed) return null;

  return (
    <>
      {/* Keyframe animations and pseudo-element effects that require CSS.
          Injected as raw HTML: as a text child, the server escapes the quotes
          to &quot; and hydration fails on the text mismatch. */}
      <style
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: `
        @property --gcc-border-angle {
          syntax: "<angle>";
          initial-value: 0deg;
          inherits: false;
        }
        @keyframes gcc-border-rotate {
          to { --gcc-border-angle: 360deg; }
        }
        .tw-sticky-inner::before {
          content: "";
          position: absolute;
          inset: 0;
          border-radius: inherit;
          padding: 1.5px;
          pointer-events: none;
          background: conic-gradient(
            from var(--gcc-border-angle),
            #0000 0, #3399ff 12%, #0000 28%,
            #0000 50%, #3399ff 62%, #0000 78%, #0000 100%
          );
          animation: gcc-border-rotate 4s linear infinite;
          -webkit-mask-image: linear-gradient(#fff 0 0), linear-gradient(#fff 0 0);
          mask-image: linear-gradient(#fff 0 0), linear-gradient(#fff 0 0);
          -webkit-mask-clip: content-box, border-box;
          mask-clip: content-box, border-box;
          -webkit-mask-origin: content-box, border-box;
          mask-origin: content-box, border-box;
          -webkit-mask-composite: xor;
          mask-composite: exclude;
        }
        @keyframes stickySlideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        @keyframes stickyFadeScale {
          from { opacity: 0; transform: translate(-50%, -47%) scale(0.97); }
          to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        }
        @keyframes live-icon-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        @keyframes live-icon-ring {
          0% { transform: scale(1); opacity: 0.7; }
          100% { transform: scale(2.6); opacity: 0; }
        }
        .tw-live-icon {
          display: inline-block;
          flex-shrink: 0;
          width: 8px;
          height: 8px;
          background: #fff;
          border-radius: 50%;
          position: relative;
          box-sizing: border-box;
          animation: live-icon-pulse 1.4s ease-out infinite;
        }
        .tw-live-icon::after {
          content: "";
          position: absolute;
          inset: 0;
          border-radius: 50%;
          background: #fff;
          animation: live-icon-ring 1.4s ease-out infinite;
        }
        .tw-sticky-drawer {
          animation: stickySlideUp 0.35s cubic-bezier(0.32, 0.72, 0, 1);
          transition: transform 0.32s cubic-bezier(0.32, 0.72, 0, 1);
        }
        .tw-sticky-drawer.is-dragging {
          transition: none;
        }
        @media (min-width: 900px) {
          .tw-sticky-drawer { display: none; }
        }
      `,
        }}
      />

      {/* Spacer */}
      <div
        className="h-[120px] min-[900px]:h-[100px] bg-[#fff] pointer-events-none"
        aria-hidden="true"
      />

      {/* Sticky Bar */}
      <div
        className="fixed bottom-0 left-0 right-0 w-full z-[300] pointer-events-none pb-[env(safe-area-inset-bottom,0px)] min-[900px]:px-4 min-[900px]:pb-[calc(env(safe-area-inset-bottom,0px)+10px)]"
        role="complementary"
        aria-label="Webinar registration"
        style={{
          transform: `translateY(${barDragY}px)`,
          transition: isBarDragging
            ? "none"
            : "transform 0.3s cubic-bezier(0.32,0.72,0,1)",
        }}
      >
        {/* Inner bar — full drag surface */}
        <div
          className="tw-sticky-inner relative w-full mx-auto px-3 py-[10px] grid grid-cols-1 items-center gap-2 shadow-[0_18px_36px_rgba(6,26,47,0.18)] backdrop-blur-[16px] pointer-events-auto bg-[rgba(19,19,19,0.6)] min-[900px]:max-w-[1100px] min-[900px]:px-[clamp(16px,2.4vw,24px)] min-[900px]:py-4 min-[900px]:grid-cols-[minmax(0,1fr)_auto] min-[900px]:grid-rows-[auto] min-[900px]:rounded-[20px]"
          onTouchStart={handleBarTouchStart}
          onTouchMove={handleBarTouchMove}
          onTouchEnd={handleBarTouchEnd}
          onMouseDown={handleBarMouseDown}
        >
          {/* Drag handle — mobile only */}
          <div
            className="col-span-full flex justify-center items-center cursor-grab touch-none select-none pointer-events-auto active:cursor-grabbing min-[900px]:hidden"
            role="button"
            aria-label="Drag up to open form"
            tabIndex={0}
          >
            <div className="w-10 h-[3px] rounded-sm bg-white transition-colors duration-200 ease-in-out group-hover:bg-white/60 group-active:bg-white/60" />
          </div>

          {/* Copy block */}
          <div
            className="flex flex-col min-w-0 col-span-full items-center min-[900px]:col-auto min-[900px]:items-start"
            onClick={handleEnquireNow}
          >
            {/* Title */}
            <div className="text-base font-bold text-white leading-[1.3] min-[900px]:text-[18px]">
              <span>Build your </span>
              <span className="text-[#ffba08]">
                1<sup>st</sup> AI Agent
              </span>
              <span> in 2 hours</span>
            </div>
            {/* Subtitle */}
            <div className="text-sm text-white mt-1 leading-[1.35] font-bold">
              <span className="inline-flex items-center gap-[5px] font-medium rounded-[6px] bg-[#ff3333] px-[6px] py-[2px]">
                <span className="tw-live-icon" />
                Live Webinar
              </span>{" "}
              {isScheduleLoading ? (
                <WebinarDateSkeleton width={180} />
              ) : (
                <>
                  {weeklyDateParts.day}
                  <sup>{weeklyDateParts.suffix}</sup>{" "}
                  {weeklyDateParts.month.slice(0, 3)}, {weeklyWebinarEntry.day}{" "}
                  at {weeklyWebinarEntry.time}
                </>
              )}
            </div>
          </div>

          {/* Register button */}
          <button
            type="button"
            onClick={handleEnquireNow}
            className="hidden min-[900px]:block flex-shrink-0 w-full bg-[#2A85FF] text-white border-none rounded-[10px] text-sm md:text-base font-bold px-2 py-[5px] cursor-pointer whitespace-nowrap shadow-[0_2px_12px_rgba(42,133,255,0.3)] transition-[transform,box-shadow,background] duration-[180ms] hover:-translate-y-px hover:shadow-[0_5px_18px_rgba(42,133,255,0.34)] hover:bg-[#1a75ef] min-[900px]:w-auto min-[900px]:min-w-[152px] min-[900px]:px-6 min-[900px]:py-2"
            aria-label="Register for Free"
            disabled={isBarDragging}
            style={{ pointerEvents: isBarDragging ? "none" : undefined }}
          >
            Register for Free
          </button>
        </div>
      </div>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/65 z-[10000] min-[900px]:z-[400]"
            onClick={() => closePanel()}
            aria-hidden="true"
            style={{ animation: "fadeIn 0.2s ease" }}
          />

          {/* Mobile bottom drawer */}
          <div
            className={`tw-sticky-drawer fixed bottom-0 left-0 right-0 z-[10001] bg-[#111] rounded-t-[20px] max-h-[90dvh] overflow-y-auto${isDragging ? " is-dragging" : ""}`}
            style={
              dragY > 0 ? { transform: `translateY(${dragY}px)` } : undefined
            }
            role="dialog"
            aria-modal="true"
            aria-label="Register for Webinar"
          >
            <div
              className="flex justify-center items-center pt-[14px] pb-2 cursor-grab touch-none select-none"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div className="w-11 h-1 rounded-sm bg-white/25" />
            </div>
            {!isDrawerSuccess && (
              <div className="text-base font-bold text-white leading-[1.3] min-[900px]:text-[18px] block text-center mt-0.5">
                <span>Build your </span>
                <span className="text-[#ffba08]">
                  1<sup>st</sup> AI Agent
                </span>
                <span> in 2 hours</span>
              </div>
            )}
            <div className="px-4 pb-8">
              <EnqueryForm
                courseId={courseID}
                pageName={pageName}
                darkBackground={true}
                submitButtonName="Register for Webinar"
                formType="Build your 1st AI-Agent in 2 hours"
                isAStepperForm
                successMessage={SuccessMessageType.ACCOUNTING}
                formHeading={FormHeading.AI_ACCOUNTING_101_WEBINAR}
                isWhatsAppLinkAvailable
                onSuccessStateChange={setIsDrawerSuccess}
              />
            </div>
          </div>

          {/* Desktop modal */}
          <div className="hidden lg:fixed lg:inset-0 lg:bg-black/80 lg:flex lg:justify-center lg:items-center lg:z-[999] lg:p-4">
            <div
              className={`rounded-2xl relative overflow-hidden mx-auto bg-black w-full max-w-[345px] ${isSuccess ? "max-w-[400px] " : "max-w-[615px] "}`}
            >
              <button
                onClick={() => closePanel(true)}
                aria-label="Close"
                className="text-[#3399ff] text-sm absolute right-2 top-2 cursor-pointer !w-7 h-7 flex items-center justify-center rounded-full border border-[#3399ff] bg-transparent z-30"
              >
                ✕
              </button>
              <div className="flex flex-col md:flex-row md:items-stretch">
                {!isSuccess && (
                  <div className="hidden md:relative md:z-0 md:w-1/2 md:flex md:items-end">
                    <Image
                      src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa/pop_upBanner.webp"
                      alt="Common Weekly Webinar - Varun Jain & Uttam Pai"
                      width={340}
                      height={420}
                      className="w-full h-auto object-contain object-bottom block"
                      loading="lazy"
                    />
                  </div>
                )}
                <div
                  className={`bg-white p-[10px] ${isSuccess ? "md:w-full" : "md:w-[50%]"}`}
                >
                  <EnqueryForm
                    pageName={pageName}
                    courseId={courseID}
                    darkBackground={false}
                    submitButtonName="Register for Webinar"
                    formType="Build your 1st AI-Agent in 2 hours"
                    formHeading={FormHeading.POP_UP_FORM}
                    successMessage={SuccessMessageType.ACCOUNTING}
                    onSuccessStateChange={setIsSuccess}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default StickyFooter;
